import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Media } from '@capacitor-community/media';

const IMAGE_EXTS = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;

// ── Progress overlay (DOM-based, tidak perlu React state) ──────────────────
let progressEl: HTMLDivElement | null = null;

export const showDownloadProgress = (progress: number, label = '') => {
  if (!progressEl) {
    progressEl = document.createElement('div');
    progressEl.style.cssText = [
      'position:fixed',
      'bottom:100px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:rgba(30,30,30,0.92)',
      'color:#fff',
      'padding:12px 20px',
      'border-radius:14px',
      'z-index:99999',
      'font-size:13px',
      'min-width:220px',
      'text-align:center',
      'box-shadow:0 4px 20px rgba(0,0,0,0.3)',
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(progressEl);
  }
  progressEl.innerHTML =
    `<div style="margin-bottom:6px">${label || 'Mengunduh...'} ${progress < 100 ? progress + '%' : ''}</div>` +
    `<div style="background:rgba(255,255,255,0.2);border-radius:4px;height:4px;overflow:hidden">` +
    `<div style="background:#FF6B35;height:100%;width:${progress}%;transition:width 0.2s"></div></div>`;
};

export const hideDownloadProgress = (message: string) => {
  if (!progressEl) return;
  progressEl.innerHTML = `<div>${message}</div>`;
  setTimeout(() => {
    progressEl?.remove();
    progressEl = null;
  }, 2500);
};

// ── Fetch dengan progress tracking via XHR ─────────────────────────────────
const fetchWithProgress = (url: string, onProgress: (pct: number) => void): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 90));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
      else reject(new Error(`HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send();
  });

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// ── Simpan gambar ke galeri (Android/iOS) ─────────────────────────────────
const saveImageToGallery = async (url: string, fileName: string) => {
  showDownloadProgress(0, 'Mengunduh gambar...');

  // Request storage permission agar bisa tulis ke galeri
  try {
    await Filesystem.requestPermissions();
  } catch { /* noop */ }

  // Step 1: Download
  let blob: Blob;
  try {
    blob = await fetchWithProgress(url, (pct) => showDownloadProgress(pct, 'Mengunduh gambar...'));
  } catch (e) {
    console.error('[downloadFile] fetch failed:', e);
    throw new Error('fetch_failed');
  }

  showDownloadProgress(90, 'Menyimpan ke galeri...');

  // Step 2: Tulis ke cache
  const tempPath = `teratask_${Date.now()}_${fileName}`;
  try {
    const base64 = await blobToBase64(blob);
    await Filesystem.writeFile({ path: tempPath, data: base64, directory: Directory.Cache, recursive: true });
  } catch (e) {
    console.error('[downloadFile] writeFile failed:', e);
    throw new Error('write_failed');
  }

  // Step 3: Ambil URI native
  let uri: string;
  try {
    const result = await Filesystem.getUri({ path: tempPath, directory: Directory.Cache });
    uri = result.uri;
    console.log('[downloadFile] uri:', uri);
  } catch (e) {
    console.error('[downloadFile] getUri failed:', e);
    throw new Error('geturi_failed');
  }

  // Step 4: Simpan ke galeri
  // Media.savePhoto di Android butuh path native tanpa prefix file://
  const nativePath = uri.startsWith('file://') ? uri.slice(7) : uri;
  try {
    await Media.savePhoto({ path: nativePath });
  } catch (e) {
    console.error('[downloadFile] savePhoto failed (nativePath):', nativePath, e);
    // Coba dengan URI penuh sebagai fallback
    try {
      await Media.savePhoto({ path: uri });
    } catch (e2) {
      console.error('[downloadFile] savePhoto fallback also failed:', e2);
      Filesystem.deleteFile({ path: tempPath, directory: Directory.Cache }).catch(() => {});
      throw new Error('savephoto_failed');
    }
  }

  await Filesystem.deleteFile({ path: tempPath, directory: Directory.Cache }).catch(() => {});
  showDownloadProgress(100, 'Tersimpan ke galeri ✓');
  hideDownloadProgress('Tersimpan ke galeri ✓');
};

// ── Public API ─────────────────────────────────────────────────────────────
export const downloadFile = async (url: string, fileName: string) => {
  if (Capacitor.isNativePlatform()) {
    const isImage = IMAGE_EXTS.test(fileName) || IMAGE_EXTS.test(url.split('?')[0]);

    if (isImage) {
      try {
        await saveImageToGallery(url, fileName);
      } catch (err: any) {
        console.error('Gallery save failed at step:', err?.message, err);
        hideDownloadProgress(`Gagal: ${err?.message || 'unknown'}`);
        window.open(url, '_system');
      }
      return;
    }

    // Non-gambar (PDF, doc, dll): buka di Chrome
    showDownloadProgress(50, 'Membuka file...');
    window.open(url, '_system');
    hideDownloadProgress('File dibuka di browser ✓');
    return;
  }

  // Web browser: blob + anchor dengan progress
  try {
    showDownloadProgress(0, 'Mengunduh...');
    const blob = await fetchWithProgress(url, (pct) => showDownloadProgress(pct, 'Mengunduh...'));
    const objectUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    showDownloadProgress(100, 'Mengunduh...');
    hideDownloadProgress('Download selesai ✓');

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    }, 10000);
  } catch (err) {
    console.error('Download failed', err);
    hideDownloadProgress('Download gagal');
    window.open(url, '_blank');
  }
};
